import produce from "immer";
import {theme as defaultTheme, DefaultTheme, ColorHues, VariantColor} from "@chakra-ui/core";

type TenantColor = Exclude<VariantColor, "black" | "white">;
type ColorMode = "light" | "dark";

// Semantic colors, usable as any Chakra color prop (`color="muted"`, `bg="card"`).
// Values are CSS variables so they switch with the `dark` class on <html>, before hydration
const TOKENS = {
  page: {light: "white", dark: "#030712"},
  card: {light: "white", dark: "#111827"},
  surface: {light: "gray.100", dark: "#1F2937"},
  toolbar: {light: "gray.50", dark: "#111827"},
  highlight: {light: "primary.50", dark: "#111827"},
  text: {light: "gray.800", dark: "#F9FAFB"},
  strong: {light: "gray.700", dark: "#E5E7EB"},
  muted: {light: "gray.600", dark: "#9CA3AF"},
  subtle: {light: "gray.500", dark: "#9CA3AF"},
  faint: {light: "gray.400", dark: "#6B7280"},
  border: {light: "gray.100", dark: "#1F2937"},
  borderStrong: {light: "gray.200", dark: "#1F2937"},
  accent: {light: "primary.500", dark: "primary.300"},
  success: {light: "green.500", dark: "#4ADE80"},
  successText: {light: "green.600", dark: "#4ADE80"},
  successStrong: {light: "green.700", dark: "#86EFAC"},
  successBg: {light: "green.50", dark: "rgba(74, 222, 128, 0.12)"},
  warningBg: {light: "#FBEEC3", dark: "rgba(251, 191, 36, 0.14)"},
  warningBgSoft: {light: "#FFF4E5", dark: "rgba(251, 191, 36, 0.14)"},
  warningText: {light: "orange.700", dark: "#FCD34D"},
  dangerText: {light: "red.600", dark: "#FCA5A5"},
};

export type Token = keyof typeof TOKENS;

// Chakra's gray scale in dark mode. Chakra itself only uses 700 (drawers, modals) and
// 800 (body) there, while our shared components use 50-600 in both modes, so the light
// half is flipped to dark tones. Light mode keeps Chakra's original grays
const DARK_GRAYS: ColorHues = {
  50: "#111827",
  100: "#1F2937",
  200: "#374151",
  300: "#4B5563",
  400: "#6B7280",
  500: "#9CA3AF",
  600: "#D1D5DB",
  700: "#111827",
  800: "#030712",
  900: "#030712",
};

const HUES = (Object.keys(DARK_GRAYS) as unknown) as (keyof ColorHues)[];

const toVariable = (name: string) => `--${name.replace(/[A-Z]/g, (char) => `-${char.toLowerCase()}`)}`;

export interface Theme extends DefaultTheme {
  colors: DefaultTheme["colors"] & {primary: ColorHues} & {[K in Token]: string};
}

const getTheme = (color?: TenantColor): Theme | DefaultTheme =>
  produce<DefaultTheme, Theme>(defaultTheme, (theme) => {
    theme.colors.primary = defaultTheme.colors[color || "cyan"];
    theme.colors.gray = HUES.reduce(
      (grays, hue) => ({...grays, [hue]: `var(--gray-${hue})`}),
      {} as ColorHues,
    );

    (Object.keys(TOKENS) as Token[]).forEach((token) => {
      theme.colors[token] = `var(${toVariable(token)})`;
    });
  });

// Resolves "gray.600" / "primary.50" against the original (hex) palette
function resolve(value: string, color?: TenantColor): string {
  const [name, hue] = value.split(".");
  const palette = name === "primary" ? defaultTheme.colors[color || "cyan"] : defaultTheme.colors[name];

  if (hue && palette && typeof palette === "object") return palette[hue];

  return typeof palette === "string" ? palette : value;
}

// CSS variables for both modes, to be injected globally under `html` and `html.dark`
export function getColorModeVariables(mode: ColorMode, color?: TenantColor): string {
  const grays = HUES.map(
    (hue) => `--gray-${hue}: ${mode === "dark" ? DARK_GRAYS[hue] : defaultTheme.colors.gray[hue]};`,
  );
  const tokens = (Object.keys(TOKENS) as Token[]).map(
    (token) => `${toVariable(token)}: ${resolve(TOKENS[token][mode], color)};`,
  );

  // Channels of `page`, for gradients and overlays that need alpha
  const pageRgb = `--page-rgb: ${mode === "dark" ? "3, 7, 18" : "255, 255, 255"};`;

  return [`color-scheme: ${mode};`, ...grays, ...tokens, pageRgb].join("\n");
}

export default getTheme;
